import {
  createContext,
  createEffect,
  createSignal,
  JSX,
  onCleanup,
  onMount,
  untrack,
  useContext,
} from 'solid-js';
import { getLogger } from '@trimble/logging';
import type { FeatureFlagsProviderProps } from './FeatureFlagsProvider.types';
import type { FeatureFlagsState } from './types';

export const FeatureFlagsContext = createContext<FeatureFlagsState>();

/**
 * Collates the flag sources in `sources` behind one `hasFlag` check. The type
 * parameter only types `sources`; give consumers a typed `useFeatureFlags<TFlag>()`
 * of their own.
 *
 * The Cloudbees (Rox) setup and targeting context are page-wide, so providers
 * mounted for different apps on one page still share them.
 */
export function FeatureFlagsProvider<TFlag extends string>(
  props: Readonly<FeatureFlagsProviderProps<TFlag>>
): JSX.Element {
  const sources = untrack(() => props.sources);
  const [isReady, setIsReady] = createSignal(false);
  let disposed = false;

  onMount(async () => {
    const results = await Promise.allSettled(
      sources.map((source) => source.initialize())
    );
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        getLogger().error(
          `Failed to initialize feature flag provider "${sources[index]?.name}":`,
          result.reason
        );
      }
    });
    if (disposed) {
      return;
    }
    setIsReady(true);
  });

  // Visible in dev or with ?log=true. Re-runs when a source's enabled set changes,
  // so the late CloudBees configuration fetch shows up as a second line.
  createEffect(() => {
    if (!isReady()) {
      return;
    }
    sources.forEach((source) => {
      getLogger().info(
        `[feature-flags] ${source.name} enabled (${source.enabledFlags().length} flags)`
      );
    });
  });

  onCleanup(() => {
    disposed = true;
    sources.forEach((source) => source.dispose?.());
  });

  const hasFlag = (flag: TFlag): boolean =>
    sources.some((source) => source.hasFlag(flag));

  return (
    <FeatureFlagsContext.Provider value={{ hasFlag, isReady }}>
      {props.children}
    </FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlags = <
  TFlag extends string = string,
>(): FeatureFlagsState<TFlag> => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider'
    );
  }
  return context;
};
